import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { DataTablesModule } from 'angular-datatables';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { MarkdownModule } from 'ngx-markdown';
import { JwtModule } from '@auth0/angular-jwt';

import { Environment } from 'environments-src';

// Third party modules
import { TranslateModule, TranslateLoader, MissingTranslationHandler, TranslateService } from '@ngx-translate/core';

// Own modules
import { WebCoreIoCKeys, Diagnostic, IoCContainer, ISettingsStorage } from '@lmnop/web-core';
import { AngularCoreIoCKeys } from '@lmnop/angular-core';

// App module
import { ApplicationRoutes } from './routes';
import * as Public from './public';

import * as AngularWebsiteCore from '@lmnop/angular-website-core';
import * as AngularCore from '@lmnop/angular-core';
import { ExtendedCookieService } from '@lmnop/angular-website-core';
import { AngularWebsiteCoreIoCKeys } from '@lmnop/angular-website-core';
import { ToastrModule } from 'ngx-toastr';
import { API_BASE_URL, CoreService } from './services/api-services';
import { NgxSpinnerModule } from 'ngx-spinner';
import { IWebsiteCoreSettings, WebsiteCoreIoCKeys } from '../../own_modules/@lmnop/website-core';

const PRIVATE_ANGULARWEBSITEMODULE_DECLARATIONS: any[] = [];

const PUBLIC_ANGULARWEBSITEMODULE_DECLARATIONS: any[] = [
    Public.AppComponent,
    // Pages and components
    Public.HomePage,
];

export function tokenGetter() {
    return AngularCore.LocalStorageWrapper.getToken();
}

export function initConfiguration(translateService: TranslateService, cookieService: ExtendedCookieService): Function {
    return async (): Promise<void> => {
        Diagnostic.debug('initConfiguration started');

        Diagnostic.debug('initConfiguration - translateService started');
        translateService.setDefaultLang('en');
        translateService.use(translateService.getBrowserCultureLang().substring(0, 2));
        IoCContainer.bindThirdPartyInstance(AngularCoreIoCKeys.AngularTranslateService, translateService);
        let angularTranslation = IoCContainer.get(AngularCoreIoCKeys.IAngularTranslation);
        await angularTranslation.waitUntilInitialized();
        Diagnostic.debug('initConfiguration - translateService ended');

        IoCContainer.bindThirdPartyInstance(AngularWebsiteCoreIoCKeys.IExtendedCookieService, cookieService);

        //Retrieving settings and settingsStorage.
        let appSettings: Public.IAppSettings = IoCContainer.get(Public.AppIoCKeys.IAppSettings);
        let coreSettings: IWebsiteCoreSettings = IoCContainer.get(WebsiteCoreIoCKeys.IWebsiteCoreSettings);
        let settingsStorage: ISettingsStorage = IoCContainer.get(WebCoreIoCKeys.ISettingsStorage);

        // todo: itt hol fusson meg a settings.init?
        appSettings.init();

        //Assign settingsStorage and version to appSettings and coreSettings.
        await settingsStorage.initSettings(appSettings, coreSettings);

        Diagnostic.debug('initConfiguration ended');
    };
}

@NgModule({
    declarations: [PRIVATE_ANGULARWEBSITEMODULE_DECLARATIONS, PUBLIC_ANGULARWEBSITEMODULE_DECLARATIONS],
    imports: [
        // Angular modules
        BrowserAnimationsModule,
        CommonModule,
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: AngularWebsiteCore.WebsiteHttpLoaderFactory,
                deps: [HttpClient],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: AngularCore.AppMissingTranslationHandler,
            },
        }),
        RouterModule.forRoot(ApplicationRoutes, {
            scrollPositionRestoration: 'enabled',
            anchorScrolling: 'enabled',
        }),
        DataTablesModule,
        NgxMaskDirective,
        NgxMaskPipe,
        HammerModule,
        NgxJsonViewerModule,
        ColorPickerModule.forRoot(),
        BsDatepickerModule.forRoot(),
        NgMultiSelectDropDownModule.forRoot(),
        TooltipModule.forRoot(),
        MarkdownModule.forRoot(),
        JwtModule.forRoot({
            config: {
                tokenGetter: tokenGetter,
                allowedDomains: [Environment.allowedDomains],
                disallowedRoutes: [],
            },
        }),
        ToastrModule.forRoot({
            preventDuplicates: true,
        }),
        NgxSpinnerModule,
        // Own modules
        AngularCore.AngularCoreModule,
        AngularWebsiteCore.AngularWebsiteCoreModule,
    ],
    providers: [
        ExtendedCookieService,
        {
            provide: APP_INITIALIZER,
            useFactory: initConfiguration,
            multi: true,
            deps: [TranslateService, ExtendedCookieService],
        },
        {
            provide: ErrorHandler,
            useClass: AngularCore.AngularErrorHandler,
        },
        { provide: API_BASE_URL, useValue: Environment.apiUrl },
        { provide: AngularCore.MODULE_ENVIRONMENTS, useValue: Environment },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AngularCore.AuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AngularCore.HttpErrorHandlerService,
            multi: true,
        },
        ExtendedCookieService,
        { provide: AngularCore.CoreService_TOKEN, useClass: CoreService },
        DatePipe,
        CurrencyPipe,
    ],
    bootstrap: [Public.AppComponent],
})
export class AppModule {}

export * from './public';
